import { useEffect, useRef, useState } from 'react';
import { useRouter } from "next/router";
import styles from "./style.module.css";
import GoogleLogo from "@assets/GoogleLogo";
import { buildOAuthAttributionState } from "@src/services/posthog/attribution";
import { captureEvent } from "@src/services/posthog/browser";
import { POSTHOG_EVENTS } from "@src/services/posthog/events";
import { getSignupAttribution, SIGNUP_VERSION } from '@src/services/auth/signup';

interface GoogleSignInProps {
  authFlow?: 'signIn' | 'signUp';
  userGroup?: string;
  disabled?: boolean;
  onBusyChange?: (busy: boolean) => void;
}

export default function GoogleSignIn({
  authFlow = 'signIn',
  userGroup = 'candidate',
  disabled = false,
  onBusyChange,
}: GoogleSignInProps) {
  const router = useRouter();
  const navigating = useRef(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    const resetAfterBack = (event: PageTransitionEvent) => {
      if (!event.persisted) return;
      navigating.current = false;
      setLoading(false);
      onBusyChange?.(false);
    };
    window.addEventListener('pageshow', resetAfterBack);
    return () => {
      window.removeEventListener('pageshow', resetAfterBack);
      onBusyChange?.(false);
    };
  }, [onBusyChange]);
  const signIn = async () => {
    if (disabled || navigating.current) return;
    navigating.current = true;
    setLoading(true);
    onBusyChange?.(true);
    setError('');
    const signupProperties = {
      user_group: userGroup,
      signupMethod: 'oauth',
      signup_version: SIGNUP_VERSION,
      authProvider: 'google',
      ...getSignupAttribution(),
    };
    if (authFlow === 'signUp') {
      captureEvent(POSTHOG_EVENTS.SIGNUP_SUBMITTED, { ...signupProperties, action: 'oauth_redirect' });
    }
    try {
      const clientId = process.env.NEXT_PUBLIC_USER_POOL_CLIENT_ID;
      if (!clientId || !process.env.NEXT_PUBLIC_OAUTH_REDIRECT_SIGNIN || !process.env.NEXT_PUBLIC_COGNITO_DOMAIN) {
        throw new Error('oauth_unavailable');
      }
      const state = buildOAuthAttributionState({
        authFlow,
        authProvider: 'google',
        userGroup,
      });
      const params = new URLSearchParams({
        redirect_uri: process.env.NEXT_PUBLIC_OAUTH_REDIRECT_SIGNIN,
        response_type: 'code',
        client_id: clientId,
        identity_provider: 'Google',
        scope: 'email openid profile',
        state,
      });

      if (authFlow === 'signUp') {
        captureEvent(POSTHOG_EVENTS.SIGNUP_STARTED, signupProperties);
      }

      const authUrl = `https://${process.env.NEXT_PUBLIC_COGNITO_DOMAIN}/oauth2/authorize?${params.toString()}`;
      // Next.js returns false after initiating a hard navigation to an external
      // URL. That is the expected OAuth handoff, not a sign-in failure.
      await router.push(authUrl);
    } catch {
      navigating.current = false;
      setLoading(false);
      onBusyChange?.(false);
      setError('Could not open Google sign-in. Try again or use your email.');
      if (authFlow === 'signUp') {
        captureEvent(POSTHOG_EVENTS.SIGNUP_FAILED, { ...signupProperties, action: 'oauth_redirect', reason: 'oauth_navigation_failed' });
      }
    }
  };
  return (
    <div className={styles.container}>
      <button type="button" className={styles.button} onClick={signIn} disabled={disabled || loading} aria-busy={loading}>
        <GoogleLogo aria-hidden="true" focusable="false" />
        <span>{loading ? 'Opening Google…' : authFlow === 'signUp' ? 'Continue with Google' : 'Sign in with Google'}</span>
      </button>
      {error && <p role="alert" className={styles.error}>{error}</p>}
    </div>
  );
}
